import React from 'react';
import { addDays, addHours, addMinutes, addWeeks, addMonths, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface IOSDateQuickButtonsProps {
  currentValue?: string;
  onChange: (value: string | undefined) => void;
  isDisabled?: boolean;
}

/**
 * iOS风格的日期快速选择按钮组件
 */
export const IOSDateQuickButtons: React.FC<IOSDateQuickButtonsProps> = ({
  currentValue,
  onChange,
  isDisabled = false
}) => {
  const { t } = useTranslation(['v3/ios', 'common']);

  // 获取基准时间（当前值或当前时间）
  const getBaseTime = (): Date => {
    if (currentValue) {
      try {
        const date = parseISO(currentValue);
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (error) {
        console.error("Error parsing current date:", error);
      }
    }
    return new Date();
  };

  // 设置为现在
  const handleSetNow = () => {
    const now = new Date();
    onChange(now.toISOString());
  };

  // 添加15分钟
  const handleAdd15Minutes = () => {
    const baseTime = getBaseTime();
    const newTime = addMinutes(baseTime, 15);
    onChange(newTime.toISOString());
  };

  // 添加1小时
  const handleAddHour = () => {
    const baseTime = getBaseTime();
    const newTime = addHours(baseTime, 1);
    onChange(newTime.toISOString());
  };

  // 添加1天
  const handleAddDay = () => {
    const baseTime = getBaseTime();
    const newTime = addDays(baseTime, 1);
    onChange(newTime.toISOString());
  };

  // 添加1周
  const handleAddWeek = () => {
    const baseTime = getBaseTime();
    const newTime = addWeeks(baseTime, 1);
    onChange(newTime.toISOString());
  };

  // 添加1月
  const handleAddMonth = () => {
    const baseTime = getBaseTime();
    const newTime = addMonths(baseTime, 1);
    onChange(newTime.toISOString());
  };

  // 清除日期
  const handleClear = () => {
    onChange(undefined);
  };

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      <button
        type="button"
        onClick={handleSetNow}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.now')}
      </button>
      <button
        type="button"
        onClick={handleAdd15Minutes}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.plus15m')}
      </button>
      <button
        type="button"
        onClick={handleAddHour}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.plus1h')}
      </button>
      <button
        type="button"
        onClick={handleAddDay}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.plus1d')}
      </button>
      <button
        type="button"
        onClick={handleAddWeek}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.plus1w')}
      </button>
      <button
        type="button"
        onClick={handleAddMonth}
        disabled={isDisabled}
        className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue rounded-full disabled:opacity-50"
      >
        {t('v3/ios:dateButtons.plus1m')}
      </button>
      {currentValue && (
        <button
          type="button"
          onClick={handleClear}
          disabled={isDisabled}
          className="px-2 py-1 text-xs bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-red dark:text-ios-dark-red rounded-full disabled:opacity-50"
        >
          {t('common:actions.clear')}
        </button>
      )}
    </div>
  );
};
